import Seo from "@/components/Seo";
import { FadeUp, ChapterLabel } from "@/components/Reveal";
import { useSiteData, defaultSettings } from "@/api";
import { AlertCircle } from "lucide-react";

const Bloc = ({ titre, children }) => (
  <FadeUp className="border-b border-ink/10 pb-12">
    <h2 className="font-serif text-2xl tracking-tight sm:text-3xl">{titre}</h2>
    <div className="mt-6 space-y-3 text-sm leading-relaxed text-bark md:text-base">{children}</div>
  </FadeUp>
);

const ACompleter = ({ children }) => (
  <span
    className="inline-flex items-center gap-1.5 rounded-sm bg-clay/10 px-2 py-0.5 font-medium text-clay"
    data-testid="champ-a-completer"
  >
    <AlertCircle size={13} /> {children}
  </span>
);

const Valeur = ({ v }) =>
  v && v.startsWith("À compléter") ? <ACompleter>{v}</ACompleter> : <>{v}</>;

const MentionsLegales = () => {
  const site = useSiteData({ chantiers: null, services: null, settings: defaultSettings });
  const s = site.settings || defaultSettings;
  const legal = { ...defaultSettings.legal, ...(s.legal || {}) };

  return (
    <>
      <Seo
        title="Mentions légales — Alexis Peinture, peintre à Angoulême & Cognac"
        description="Mentions légales du site Alexis Peinture, artisan peintre en bâtiment dans le Grand Angoulême et le Grand Cognac."
      />
      <section className="mx-auto max-w-4xl px-6 pb-32 pt-40 lg:pt-48">
        <ChapterLabel number="—" label="Informations légales" />
        <FadeUp delay={0.1}>
          <h1 className="mt-8 font-serif text-4xl tracking-tight sm:text-5xl" data-testid="mentions-title">
            Mentions <em className="italic text-clay">légales</em>
          </h1>
        </FadeUp>

        <div className="mt-16 space-y-12">
          <Bloc titre="Éditeur du site">
            <p>
              <strong className="text-ink">{s.entreprise}</strong> — {s.gerant}
            </p>
            <p>Forme juridique : <Valeur v={legal.formeJuridique} /></p>
            <p>SIRET : <Valeur v={legal.siret} /></p>
            <p>Adresse du siège : <Valeur v={legal.adresse} /></p>
            <p>
              Téléphone : {s.telephone} — Email :{" "}
              <a href={`mailto:${s.email}`} className="text-clay underline-offset-2 hover:underline">
                {s.email}
              </a>
            </p>
            <p>Zone d'intervention : {s.zone}</p>
          </Bloc>

          <Bloc titre="Directeur de la publication">
            <p>{s.gerant}, gérant de {s.entreprise}.</p>
          </Bloc>

          <Bloc titre="Hébergement">
            <p>Ce site est hébergé par la plateforme Emergent (emergent.sh).</p>
          </Bloc>

          <Bloc titre="Propriété intellectuelle">
            <p>
              L'ensemble des contenus du site (textes, visuels, logo, structure)
              est la propriété d'{s.entreprise}, sauf mention contraire. Toute
              reproduction sans autorisation écrite est interdite.
            </p>
          </Bloc>

          <Bloc titre="Données personnelles">
            <p>
              Les informations transmises via le formulaire de devis sont
              traitées conformément à notre{" "}
              <a href="/confidentialite" data-testid="mentions-link-confidentialite" className="text-clay underline-offset-2 hover:underline">
                politique de confidentialité
              </a>
              .
            </p>
          </Bloc>
        </div>
      </section>
    </>
  );
};

export default MentionsLegales;
